
import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import {
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";

import {
  Navbar,
  Nav,
  NavDropdown,
  Container,
  Button,
  Offcanvas,
  Form,
} from "react-bootstrap";

import {
  FaShoppingCart,
  FaSearch,
  FaArrowRight,
  FaTimes,
} from "react-icons/fa";

import {
  FiUser,
  FiMenu,
  FiX,
  FiShoppingBag,
} from "react-icons/fi";

import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";


// ============================================================
// API
// ============================================================

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") ||
  "https://apimyshop.myidealtd.co.uk";


// ============================================================
// NAVBAR
// ============================================================

const NavBar = () => {

  const {
    user,
    logout,
    authLoaded,
  } = useAuth();

  const {
    cart = [],
  } = useCart();

  const navigate = useNavigate();
  const location = useLocation();


  // ==========================================================
  // STATE
  // ==========================================================

  const [expanded, setExpanded] =
    useState(false);

  const [showCart, setShowCart] =
    useState(false);

  const [search, setSearch] =
    useState("");


  // ==========================================================
  // SAFE CART
  // ==========================================================

  const safeCart =
    Array.isArray(cart)
      ? cart
      : [];


  // ==========================================================
  // CART ITEM COUNT
  // ==========================================================

  const cartItemCount = useMemo(() => {

    return safeCart.reduce(
      (total, item) => {

        const quantity = Number(
          item?.quantity ??
          item?.qty ??
          1
        );

        return (
          total +
          (
            Number.isFinite(quantity)
              ? quantity
              : 1
          )
        );

      },
      0
    );

  }, [safeCart]);


  // ==========================================================
  // CART TOTAL
  // ==========================================================

  const cartTotal = useMemo(() => {

    return safeCart.reduce(
      (total, item) => {

        const price = Number(
          item?.salePrice ??
          item?.price ??
          0
        );

        const quantity = Number(
          item?.quantity ??
          item?.qty ??
          1
        );

        const safePrice =
          Number.isFinite(price)
            ? price
            : 0;

        const safeQuantity =
          Number.isFinite(quantity)
            ? quantity
            : 1;

        return (
          total +
          safePrice *
          safeQuantity
        );

      },
      0
    );

  }, [safeCart]);


  // ==========================================================
  // CLOSE MOBILE MENU ON ROUTE CHANGE
  // ==========================================================

  useEffect(() => {

    setExpanded(false);
    setSearch("");

  }, [location.pathname]);


  // ==========================================================
  // CLOSE MOBILE NAV
  // ==========================================================

  const closeMobileNav = () => {
    setExpanded(false);
  };


  // ==========================================================
  // LOGOUT
  // ==========================================================

  const handleLogout = async () => {

    try {

      await logout();

    } catch (error) {

      console.error(
        "Logout error:",
        error
      );

    }

    setExpanded(false);
    setShowCart(false);

    navigate("/login");
  };


  // ==========================================================
  // SEARCH
  // ==========================================================

  const handleSearch = (e) => {

    e.preventDefault();

    const query =
      search.trim();

    if (!query) {
      return;
    }

    navigate(
      "/products?q=" +
      encodeURIComponent(query)
    );

    setSearch("");
    setExpanded(false);
  };


  // ==========================================================
  // OPEN CART
  // ==========================================================

  const openCart = () => {

    setExpanded(false);
    setShowCart(true);

  };


  // ==========================================================
  // PRODUCT IMAGE
  // ==========================================================

  const getProductImage = (item) => {

    const images =
      item?.images;

    if (
      !Array.isArray(images) ||
      images.length === 0
    ) {

      return "/images/no-image.png";

    }

    const firstImage =
      images[0];

    let imagePath = null;


    // --------------------------------------------------------
    // NEW OBJECT FORMAT
    // --------------------------------------------------------

    if (
      firstImage &&
      typeof firstImage === "object"
    ) {

      imagePath =
        firstImage.main ||
        firstImage.cart ||
        firstImage.thumbnail ||
        firstImage.original ||
        null;

    }


    // --------------------------------------------------------
    // OLD STRING FORMAT
    // --------------------------------------------------------

    if (
      !imagePath &&
      typeof firstImage === "string"
    ) {

      imagePath =
        firstImage;

    }


    if (!imagePath) {

      return "/images/no-image.png";

    }


    // --------------------------------------------------------
    // ABSOLUTE URL
    // --------------------------------------------------------

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://") ||
      imagePath.startsWith("data:")
    ) {

      return imagePath;

    }


    // --------------------------------------------------------
    // NORMALISE
    // --------------------------------------------------------

    if (
      !imagePath.startsWith("/")
    ) {

      imagePath =
        "/" + imagePath;

    }


    return API_BASE + imagePath;

  };


  // ==========================================================
  // IMAGE FALLBACK
  // ==========================================================

  const handleImageError = (e) => {

    if (
      e.currentTarget.dataset
        .fallbackApplied === "true"
    ) {

      return;

    }

    e.currentTarget.dataset
      .fallbackApplied = "true";

    e.currentTarget.src =
      "/images/no-image.png";

  };


  // ==========================================================
  // AUTH LOADING
  // ==========================================================

  if (!authLoaded) {

    return (

      <Navbar
        sticky="top"
        className="navBar"
      >

        <Container fluid>

          <Navbar.Brand
            className="logo"
          >
            My<span>Shop</span>
          </Navbar.Brand>

        </Container>

      </Navbar>

    );

  }


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <>

      {/* ====================================================
          MAIN NAVBAR
      ===================================================== */}

      <Navbar
        expand="lg"
        expanded={expanded}
        onToggle={setExpanded}
        sticky="top"
        className="navBar"
      >

        <Container
          fluid
          className="navbarContainer"
        >


          {/* =================================================
              LOGO
          ================================================== */}

          <Navbar.Brand
            as={Link}
            to="/"
            className="logo"
            onClick={closeMobileNav}
          >

            My<span>Shop</span>

          </Navbar.Brand>


          {/* =================================================
              MOBILE CART QUICK ACCESS
          ================================================== */}

          <div className="mobileActions">

            {!user?.isAdmin && (

              <button
                type="button"
                className="mobileCartButton"
                onClick={openCart}
                aria-label="Open shopping cart"
              >

                <FaShoppingCart />

                {cartItemCount > 0 && (

                  <span className="mobileCartBadge">
                    {cartItemCount}
                  </span>

                )}

              </button>

            )}


            {/* =================================================
                CUSTOM HAMBURGER
            ================================================== */}

            <Navbar.Toggle
              aria-controls="main-navbar"
              className="customNavbarToggle"
            >

              {expanded ? (
                <FiX />
              ) : (
                <FiMenu />
              )}

            </Navbar.Toggle>

          </div>


          {/* =================================================
              NAVBAR COLLAPSE
          ================================================== */}

          <Navbar.Collapse
            id="main-navbar"
            className="mainNavbarCollapse"
          >

            <Nav className="navRight">


              {/* =================================================
                  HOME
              ================================================= */}

              <Nav.Link
                as={Link}
                to="/"
                className="navItem"
                onClick={closeMobileNav}
              >
                Home
              </Nav.Link>


              {/* =================================================
                  SHOP
              ================================================= */}

              <NavDropdown
                title="Shop"
                id="shop-dropdown"
                className="navDropdown"
              >

                <NavDropdown.Item
                  as={Link}
                  to="/products"
                  onClick={closeMobileNav}
                >
                  <span>🛍️</span>
                  All Products
                </NavDropdown.Item>


                <NavDropdown.Item
                  as={Link}
                  to="/new-arrivals"
                  onClick={closeMobileNav}
                >
                  <span>🆕</span>
                  New Arrivals
                </NavDropdown.Item>


                <NavDropdown.Item
                  as={Link}
                  to="/best-sellers"
                  onClick={closeMobileNav}
                >
                  <span>🏆</span>
                  Best Sellers
                </NavDropdown.Item>


                <NavDropdown.Item
                  as={Link}
                  to="/sale"
                  onClick={closeMobileNav}
                >
                  <span>🔥</span>
                  Sale
                </NavDropdown.Item>

              </NavDropdown>


              {/* =================================================
                  CATEGORIES
              ================================================== */}

              <Nav.Link
                as={Link}
                to="/categories"
                className="navItem"
                onClick={closeMobileNav}
              >
                Categories
              </Nav.Link>


              {/* =================================================
                  ABOUT
              ================================================== */}

              <Nav.Link
                as={Link}
                to="/about"
                className="navItem"
                onClick={closeMobileNav}
              >
                About
              </Nav.Link>


              {/* =================================================
                  CONTACT
              ================================================== */}

              <Nav.Link
                as={Link}
                to="/contact"
                className="navItem"
                onClick={closeMobileNav}
              >
                Contact
              </Nav.Link>


              {/* =================================================
                  SEARCH
              ================================================== */}

              <Form
                onSubmit={handleSearch}
                className="searchBox"
              >

                <div className="searchWrapper">

                  <FaSearch
                    className="searchIcon"
                  />

                  <input
                    type="search"
                    value={search}
                    onChange={(e) =>
                      setSearch(
                        e.target.value
                      )
                    }
                    placeholder="Search products..."
                    className="searchInput"
                    aria-label="Search products"
                  />

                </div>

              </Form>


              {/* =================================================
                  USER
              ================================================== */}

              {user &&
                !user?.isAdmin && (

                <div className="userName">

                  <FiUser
                    className="userIcon"
                  />

                  <span>
                    {
                      user.username ||
                      user.name ||
                      user.fullName ||
                      user.email
                    }
                  </span>

                </div>

              )}


              {/* =================================================
                  DESKTOP CART
              ================================================== */}

              {!user?.isAdmin && (

                <button
                  type="button"
                  onClick={openCart}
                  className="cartNavButton"
                  aria-label="Open shopping cart"
                >

                  <FaShoppingCart />

                  <span className="cartText">
                    Cart
                  </span>

                  {cartItemCount > 0 && (

                    <span className="cartBadge">
                      {cartItemCount}
                    </span>

                  )}

                </button>

              )}


              {/* =================================================
                  ADMIN
              ================================================== */}

              {user?.isAdmin && (

                <NavDropdown
                  title={
                    <span className="adminBtn">

                      <FiUser />

                      <span>
                        Admin
                      </span>

                    </span>
                  }
                  id="admin-dropdown"
                  align="end"
                  className="navDropdown"
                >

                  <NavDropdown.Item
                    as={Link}
                    to="/admin/main"
                    onClick={closeMobileNav}
                  >
                    📊 Dashboard
                  </NavDropdown.Item>


                  <NavDropdown.Item
                    as={Link}
                    to="/addproduct"
                    onClick={closeMobileNav}
                  >
                    ➕ Add Product
                  </NavDropdown.Item>


                  <NavDropdown.Item
                    as={Link}
                    to="/admin/newsletter"
                    onClick={closeMobileNav}
                  >
                    📧 News Letters
                  </NavDropdown.Item>


                  <NavDropdown.Item
                    as={Link}
                    to="/admin/bulk-upload"
                    onClick={closeMobileNav}
                  >
                    📦 Bulk Upload
                  </NavDropdown.Item>


                  <NavDropdown.Item
                    as={Link}
                    to="/admin/banners"
                    onClick={closeMobileNav}
                  >
                    🖼️ Banners
                  </NavDropdown.Item>


                  <NavDropdown.Item
                    as={Link}
                    to="/productlist"
                    onClick={closeMobileNav}
                  >
                    🛍️ Products
                  </NavDropdown.Item>


                  <NavDropdown.Divider />


                  <NavDropdown.Item
                    onClick={handleLogout}
                  >
                    🚪 Logout
                  </NavDropdown.Item>

                </NavDropdown>

              )}


              {/* =================================================
                  USER LOGOUT
              ================================================== */}

              {user &&
                !user?.isAdmin && (

                <Button
                  type="button"
                  onClick={handleLogout}
                  className="logoutBtn"
                >

                  Logout

                </Button>

              )}


              {/* =================================================
                  LOGIN
              ================================================== */}

              {!user && (

                <Nav.Link
                  as={Link}
                  to="/login"
                  className="loginBtn"
                  onClick={closeMobileNav}
                >

                  <FiUser />

                  <span>
                    Login
                  </span>

                </Nav.Link>

              )}

            </Nav>

          </Navbar.Collapse>

        </Container>

      </Navbar>


      {/* ====================================================
          CART OFFCANVAS
      ===================================================== */}

      <Offcanvas
        show={showCart}
        onHide={() =>
          setShowCart(false)
        }
        placement="end"
        className="premiumCart"
      >

        <Offcanvas.Header
          closeButton
          className="cartHeader"
        >

          <Offcanvas.Title
            className="cartTitle"
          >

            <span>
              🛒
            </span>

            Shopping Cart

            <span className="cartItemsPill">
              {cartItemCount} Items
            </span>

          </Offcanvas.Title>

        </Offcanvas.Header>


        {/* ==================================================
            CART BODY
        =================================================== */}

        <Offcanvas.Body
          className="cartBody"
        >

          {safeCart.length === 0 ? (

            <div className="emptyCart">

              <div className="emptyCartIcon">
                🛍️
              </div>

              <h4>
                Your cart is empty
              </h4>

              <p>
                Add something amazing.
              </p>

              <Button
                variant="dark"
                className="rounded-pill px-4"
                onClick={() =>
                  setShowCart(false)
                }
              >
                Continue Shopping
              </Button>

            </div>

          ) : (

            <>

              {/* ============================================
                  PRODUCTS
              ============================================= */}

              <div className="cartProducts">

                {safeCart.map(
                  (item, index) => {

                    const imageUrl =
                      getProductImage(item);

                    const price =
                      Number(
                        item?.salePrice ??
                        item?.price ??
                        0
                      );

                    const quantity =
                      Number(
                        item?.quantity ??
                        item?.qty ??
                        1
                      );

                    return (

                      <div
                        key={
                          item?._id ||
                          item?.productId ||
                          item?.id ||
                          index
                        }
                        className="cartProduct"
                      >

                        {/* IMAGE */}

                        <div className="cartProductImage">

                          <img
                            src={imageUrl}
                            alt={
                              item?.name ||
                              "Product"
                            }
                            onError={
                              handleImageError
                            }
                          />

                        </div>


                        {/* DETAILS */}

                        <div className="cartProductDetails">

                          <h6>
                            {
                              item?.name ||
                              "Product"
                            }
                          </h6>


                          {item?.brand && (

                            <div className="cartBrand">
                              {item.brand}
                            </div>

                          )}


                          <div className="cartPrice">

                            £
                            {
                              Number.isFinite(price)
                                ? price.toFixed(2)
                                : "0.00"
                            }

                          </div>


                          {quantity > 1 && (

                            <div className="cartQuantity">
                              Qty: {quantity}
                            </div>

                          )}

                        </div>

                      </div>

                    );

                  }
                )}

              </div>


              {/* ============================================
                  CART FOOTER
              ============================================= */}

              <div className="cartFooter">

                <div className="cartTotal">

                  <span>
                    Total
                  </span>

                  <strong>
                    £{cartTotal.toFixed(2)}
                  </strong>

                </div>


                <Button
                  variant="light"
                  className="continueBtn"
                  onClick={() =>
                    setShowCart(false)
                  }
                >
                  Continue Shopping
                </Button>


                <Button
                  className="checkoutBtn"
                  onClick={() => {

                    setShowCart(false);

                    navigate("/cart");

                  }}
                >

                  Secure Checkout

                  <FaArrowRight />

                </Button>

              </div>

            </>

          )}

        </Offcanvas.Body>

      </Offcanvas>


      {/* ====================================================
          CSS
      ===================================================== */}

      <style>{`

        /* ====================================================
           ROOT NAVBAR
        ==================================================== */

        .navBar {

          position: sticky;

          top: 0;

          z-index: 1050;

          min-height: 70px;

          background:
            rgba(10, 15, 28, 0.96);

          backdrop-filter:
            blur(24px);

          -webkit-backdrop-filter:
            blur(24px);

          border-bottom:
            1px solid
            rgba(255,255,255,0.10);

          box-shadow:
            0 10px 35px
            rgba(0,0,0,0.28);

        }


        .navbarContainer {

          min-height: 70px;

          padding-left: 24px;

          padding-right: 24px;

          gap: 20px;

        }


        /* ====================================================
           LOGO
        ==================================================== */

        .logo {

          flex-shrink: 0;

          font-size: 23px;

          font-weight: 900;

          letter-spacing: -0.6px;

          color: #fff !important;

          text-decoration: none;

          transition:
            transform .2s ease;

        }


        .logo:hover {

          transform:
            translateY(-1px);

        }


        .logo span {

          background:
            linear-gradient(
              90deg,
              #4f8cff,
              #8b5cf6
            );

          -webkit-background-clip:
            text;

          background-clip:
            text;

          -webkit-text-fill-color:
            transparent;

        }


        /* ====================================================
           MOBILE ACTION AREA
        ==================================================== */

        .mobileActions {

          display: none;

          align-items: center;

          gap: 8px;

          margin-left: auto;

        }


        /* ====================================================
           CUSTOM HAMBURGER
        ==================================================== */

        .customNavbarToggle {

          width: 46px;

          height: 46px;

          padding: 0;

          display: flex;

          align-items: center;

          justify-content: center;

          border:
            1px solid
            rgba(255,255,255,0.15) !important;

          border-radius: 14px;

          background:
            rgba(255,255,255,0.08) !important;

          color: #fff !important;

          box-shadow: none !important;

        }


        .customNavbarToggle svg {

          width: 23px;

          height: 23px;

          stroke-width: 2.2;

        }


        .customNavbarToggle:hover {

          background:
            rgba(255,255,255,0.14) !important;

          border-color:
            rgba(96,165,250,0.55) !important;

        }


        /* ====================================================
           MOBILE CART
        ==================================================== */

        .mobileCartButton {

          position: relative;

          width: 46px;

          height: 46px;

          border: 0;

          border-radius: 14px;

          display: flex;

          align-items: center;

          justify-content: center;

          background:
            rgba(255,255,255,0.08);

          color: #fff;

          cursor: pointer;

          transition:
            all .2s ease;

        }


        .mobileCartButton:hover {

          background:
            rgba(255,255,255,0.15);

        }


        .mobileCartButton svg {

          font-size: 19px;

        }


        .mobileCartBadge {

          position: absolute;

          top: -5px;

          right: -5px;

          min-width: 21px;

          height: 21px;

          padding: 0 5px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius: 50px;

          background:
            linear-gradient(
              135deg,
              #2563eb,
              #7c3aed
            );

          border:
            2px solid
            #0a0f1c;

          color: #fff;

          font-size: 10px;

          font-weight: 800;

          line-height: 1;

        }


        /* ====================================================
           COLLAPSE
        ==================================================== */

        .mainNavbarCollapse {

          flex-grow: 1;

        }


        /* ====================================================
           NAVIGATION
        ==================================================== */

        .navRight {

          display: flex;

          align-items: center;

          justify-content: flex-end;

          gap: 7px;

          width: 100%;

        }


        .navItem {

          position: relative;

          color:
            rgba(255,255,255,.88) !important;

          font-size: 14px;

          font-weight: 650;

          padding:
            10px 12px !important;

          border-radius: 10px;

          white-space: nowrap;

          transition:
            all .2s ease;

        }


        .navItem:hover {

          color:
            #fff !important;

          background:
            rgba(255,255,255,.07);

        }


        .navItem.active {

          color:
            #fff !important;

        }


        /* ====================================================
           DROPDOWNS
        ==================================================== */

        .navDropdown > .dropdown-toggle {

          color:
            rgba(255,255,255,.88) !important;

          font-size: 14px;

          font-weight: 650;

          padding:
            10px 12px !important;

          border-radius: 10px;

        }


        .navDropdown > .dropdown-toggle:hover {

          background:
            rgba(255,255,255,.07);

          color: #fff !important;

        }


        .dropdown-menu {

          margin-top: 10px !important;

          min-width: 220px;

          padding: 9px;

          background:
            rgba(15,23,42,.98) !important;

          border:
            1px solid
            rgba(255,255,255,.10) !important;

          border-radius: 16px !important;

          box-shadow:
            0 20px 50px
            rgba(0,0,0,.40);

          backdrop-filter:
            blur(20px);

        }


        .dropdown-item {

          display: flex;

          align-items: center;

          gap: 10px;

          padding:
            11px 13px;

          color:
            rgba(255,255,255,.90) !important;

          border-radius: 10px;

          font-size: 14px;

          font-weight: 550;

          transition:
            all .18s ease;

        }


        .dropdown-item:hover {

          color: #fff !important;

          background:
            linear-gradient(
              135deg,
              rgba(37,99,235,.95),
              rgba(124,58,237,.95)
            ) !important;

          transform:
            translateX(2px);

        }


        .dropdown-divider {

          border-color:
            rgba(255,255,255,.10);

        }


        /* ====================================================
           SEARCH
        ==================================================== */

        .searchBox {

          width: min(100%, 300px);

          margin:
            0 4px;

        }


        .searchWrapper {

          position: relative;

          width: 100%;

        }


        .searchIcon {

          position: absolute;

          left: 15px;

          top: 50%;

          transform:
            translateY(-50%);

          color:
            rgba(255,255,255,.55);

          pointer-events: none;

          z-index: 2;

        }


        .searchInput {

          width: 100%;

          height: 42px;

          padding:
            0 15px 0 43px;

          border:
            1px solid
            rgba(255,255,255,.13);

          border-radius: 999px;

          background:
            rgba(255,255,255,.07);

          color: #fff;

          outline: none;

          font-size: 13px;

          transition:
            all .2s ease;

        }


        .searchInput::placeholder {

          color:
            rgba(255,255,255,.48);

        }


        .searchInput:focus {

          border-color:
            rgba(79,140,255,.85);

          background:
            rgba(255,255,255,.10);

          box-shadow:
            0 0 0 4px
            rgba(79,140,255,.12);

        }


        /* ====================================================
           USER
        ==================================================== */

        .userName {

          min-width: 0;

          max-width: 190px;

          display: flex;

          align-items: center;

          gap: 8px;

          padding:
            8px 12px;

          border:
            1px solid
            rgba(255,255,255,.10);

          border-radius: 999px;

          background:
            rgba(255,255,255,.06);

          color: #fff;

          font-size: 13px;

          font-weight: 650;

        }


        .userName span {

          overflow: hidden;

          text-overflow: ellipsis;

          white-space: nowrap;

        }


        .userIcon {

          flex-shrink: 0;

          color:
            #60a5fa;

          font-size: 18px;

        }


        /* ====================================================
           CART NAV BUTTON
        ==================================================== */

        .cartNavButton {

          position: relative;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 7px;

          min-height: 42px;

          padding:
            8px 13px;

          border:
            1px solid
            rgba(255,255,255,.12);

          border-radius: 12px;

          background:
            rgba(255,255,255,.06);

          color: #fff;

          cursor: pointer;

          font-size: 14px;

          font-weight: 650;

          transition:
            all .2s ease;

        }


        .cartNavButton:hover {

          background:
            rgba(255,255,255,.12);

          transform:
            translateY(-1px);

        }


        .cartNavButton svg {

          font-size: 17px;

        }


        .cartBadge {

          position: absolute;

          top: -8px;

          right: -8px;

          min-width: 22px;

          height: 22px;

          display: flex;

          align-items: center;

          justify-content: center;

          padding: 0 5px;

          border-radius: 50px;

          background:
            linear-gradient(
              135deg,
              #2563eb,
              #7c3aed
            );

          border:
            2px solid
            #0a0f1c;

          color: #fff;

          font-size: 10px;

          font-weight: 800;

          line-height: 1;

          z-index: 10;

        }


        /* ====================================================
           ADMIN
        ==================================================== */

        .adminBtn {

          display: inline-flex;

          align-items: center;

          gap: 7px;

          color: #fff;

        }


        /* ====================================================
           LOGIN
        ==================================================== */

        .loginBtn {

          display: flex !important;

          align-items: center;

          gap: 7px;

          color: #fff !important;

          font-weight: 650;

          padding:
            9px 13px !important;

          border-radius: 11px;

        }


        .loginBtn:hover {

          background:
            rgba(255,255,255,.08);

        }


        /* ====================================================
           LOGOUT
        ==================================================== */

        .logoutBtn {

          min-height: 40px;

          border-radius: 999px !important;

          border:
            1px solid
            rgba(255,255,255,.14) !important;

          background:
            rgba(255,255,255,.06) !important;

          color: #fff !important;

          padding:
            7px 15px !important;

          font-size: 13px;

          font-weight: 650;

          box-shadow: none !important;

        }


        .logoutBtn:hover {

          background:
            rgba(239,68,68,.16) !important;

          border-color:
            rgba(248,113,113,.35) !important;

        }


        /* ====================================================
           CART OFFCANVAS
        ==================================================== */

        .premiumCart {

          --bs-offcanvas-width:
            480px;

        }


        .cartHeader {

          border: 0;

          padding: 22px;

          color: #fff;

          background:
            linear-gradient(
              135deg,
              #0f172a 0%,
              #1e293b 50%,
              #2563eb 100%
            );

        }


        .cartHeader .btn-close {

          filter:
            invert(1);

          opacity: .85;

        }


        .cartTitle {

          display: flex;

          align-items: center;

          flex-wrap: wrap;

          gap: 9px;

          font-size: 23px;

          font-weight: 800;

        }


        .cartItemsPill {

          padding:
            5px 11px;

          border-radius: 999px;

          background:
            rgba(255,255,255,.16);

          font-size: 12px;

          font-weight: 650;

        }


        .cartBody {

          min-height: 0;

          display: flex;

          flex-direction: column;

          padding: 18px;

          background:
            #f6f8fc;

        }


        /* ====================================================
           EMPTY CART
        ==================================================== */

        .emptyCart {

          flex: 1;

          min-height: 400px;

          display: flex;

          align-items: center;

          justify-content: center;

          flex-direction: column;

          text-align: center;

          color: #6b7280;

        }


        .emptyCartIcon {

          font-size: 65px;

          margin-bottom: 10px;

        }


        .emptyCart h4 {

          color: #111827;

          font-weight: 800;

        }


        /* ====================================================
           CART PRODUCTS
        ==================================================== */

        .cartProducts {

          flex: 1;

          min-height: 0;

          overflow-y: auto;

          padding-right: 3px;

        }


        .cartProduct {

          display: flex;

          align-items: center;

          gap: 14px;

          padding: 13px;

          margin-bottom: 12px;

          background:
            rgba(255,255,255,.86);

          border:
            1px solid
            rgba(229,231,235,.9);

          border-radius: 18px;

          box-shadow:
            0 7px 25px
            rgba(15,23,42,.06);

        }


        .cartProductImage {

          width: 82px;

          height: 82px;

          min-width: 82px;

          display: flex;

          align-items: center;

          justify-content: center;

          padding: 7px;

          overflow: hidden;

          background: #fff;

          border:
            1px solid #e5e7eb;

          border-radius: 15px;

        }


        .cartProductImage img {

          width: 100%;

          height: 100%;

          object-fit: contain;

        }


        .cartProductDetails {

          min-width: 0;

          flex: 1;

        }


        .cartProductDetails h6 {

          margin: 0;

          color: #111827;

          font-size: 15px;

          font-weight: 750;

          white-space: nowrap;

          overflow: hidden;

          text-overflow: ellipsis;

        }


        .cartBrand {

          margin-top: 4px;

          color: #6b7280;

          font-size: 12px;

        }


        .cartPrice {

          margin-top: 6px;

          color: #2563eb;

          font-size: 18px;

          font-weight: 800;

        }


        .cartQuantity {

          margin-top: 3px;

          color: #6b7280;

          font-size: 12px;

        }


        /* ====================================================
           CART FOOTER
        ==================================================== */

        .cartFooter {

          flex-shrink: 0;

          padding-top: 16px;

          border-top:
            1px solid #e5e7eb;

        }


        .cartTotal {

          display: flex;

          align-items: center;

          justify-content: space-between;

          margin-bottom: 16px;

          color: #111827;

          font-size: 21px;

          font-weight: 800;

        }


        .continueBtn {

          width: 100%;

          margin-bottom: 10px;

          padding: 12px;

          border-radius: 999px !important;

          font-weight: 650;

        }


        .checkoutBtn {

          width: 100%;

          display: flex;

          align-items: center;

          justify-content: center;

          gap: 8px;

          padding: 13px;

          border: 0 !important;

          border-radius: 999px !important;

          background:
            linear-gradient(
              135deg,
              #2563eb,
              #7c3aed
            ) !important;

          box-shadow:
            0 12px 30px
            rgba(37,99,235,.28);

          font-weight: 750;

        }


        /* ====================================================
           TABLET
        ==================================================== */

        @media (max-width: 1199px) {

          .navbarContainer {

            padding-left: 18px;

            padding-right: 18px;

          }


          .navRight {

            gap: 3px;

          }


          .navItem,
          .navDropdown > .dropdown-toggle {

            padding-left: 8px !important;

            padding-right: 8px !important;

          }


          .searchBox {

            width: 220px;

          }


          .userName {

            max-width: 150px;

          }

        }


        /* ====================================================
           MOBILE / TABLET
        ==================================================== */

        @media (max-width: 991.98px) {

          .navBar {

            min-height: 64px;

          }


          .navbarContainer {

            min-height: 64px;

            padding:
              9px 14px;

            flex-wrap: wrap;

          }


          .logo {

            font-size: 22px;

          }


          .mobileActions {

            display: flex;

          }


          .mainNavbarCollapse {

            flex-basis: 100%;

            width: 100%;

          }


          .navRight {

            width: 100%;

            display: flex;

            flex-direction: column;

            align-items: stretch;

            gap: 5px;

            margin-top: 12px;

            padding:
              12px 0 8px;

            border-top:
              1px solid
              rgba(255,255,255,.08);

          }


          .navItem {

            width: 100%;

            padding:
              13px 14px !important;

            border-radius: 12px;

          }


          .navItem:hover {

            background:
              rgba(255,255,255,.08);

          }


          .navDropdown {

            width: 100%;

          }


          .navDropdown > .dropdown-toggle {

            display: flex;

            align-items: center;

            justify-content: space-between;

            width: 100%;

            padding:
              13px 14px !important;

          }


          .dropdown-menu {

            width: 100%;

            margin-top: 3px !important;

            border-radius: 14px !important;

          }


          .searchBox {

            width: 100%;

            max-width: none;

            margin:
              8px 0;

          }


          .searchInput {

            height: 46px;

          }


          .userName {

            width: 100%;

            max-width: none;

            justify-content: flex-start;

            padding:
              12px 14px;

            border-radius: 12px;

          }


          .cartNavButton {

            width: 100%;

            justify-content: flex-start;

            padding:
              13px 14px;

            border-radius: 12px;

            background:
              rgba(255,255,255,.06);

          }


          .cartText {

            display: inline;

          }


          .cartBadge {

            position: static;

            min-width: 23px;

            height: 23px;

            margin-left: auto;

            border: 0;

          }


          .logoutBtn {

            width: 100%;

            margin-top: 3px;

            padding:
              12px !important;

          }


          .loginBtn {

            width: 100%;

            padding:
              13px 14px !important;

            border-radius: 12px;

          }


          .adminBtn {

            width: 100%;

            justify-content: flex-start;

          }

        }


        /* ====================================================
           SMALL MOBILE
        ==================================================== */

        @media (max-width: 576px) {

          .navbarContainer {

            padding:
              8px 12px;

          }


          .logo {

            font-size: 21px;

          }


          .customNavbarToggle,
          .mobileCartButton {

            width: 43px;

            height: 43px;

            border-radius: 13px;

          }


          .premiumCart {

            --bs-offcanvas-width:
              100vw;

          }


          .cartHeader {

            padding:
              17px;

          }


          .cartTitle {

            font-size: 20px;

          }


          .cartBody {

            padding:
              13px;

          }


          .cartProduct {

            gap: 11px;

            padding: 10px;

            border-radius: 15px;

          }


          .cartProductImage {

            width: 70px;

            height: 70px;

            min-width: 70px;

          }


          .cartProductDetails h6 {

            font-size: 14px;

          }


          .cartPrice {

            font-size: 16px;

          }

        }


        /* ====================================================
           VERY SMALL PHONES
        ==================================================== */

        @media (max-width: 380px) {

          .logo {

            font-size: 19px;

          }


          .mobileActions {

            gap: 5px;

          }


          .customNavbarToggle,
          .mobileCartButton {

            width: 40px;

            height: 40px;

          }


          .cartItemsPill {

            font-size: 10px;

            padding:
              4px 8px;

          }

        }

      `}</style>

    </>

  );

};


export default NavBar;

